import { useEffect, useState } from 'react';
import { storeService } from '../../services/storeService';
import { useCartStore } from '../../store/cartStore';
import { CartItem } from '../../models';

export type StockCheckStatus = 'checking' | 'done' | 'failed';
export type ItemStockIssue = 'soldOut' | 'overStock' | null;

const STOCK_CHECK_TIMEOUT_MS = 10000;
const NO_VARIANT_ID = 0;

export function getItemStockIssue(item: CartItem): ItemStockIssue {
  if (item.variantId !== NO_VARIANT_ID && item.stockQuantity <= 0) return 'soldOut';
  if (item.quantity > item.stockQuantity) return 'overStock';
  return null;
}

export function useCartStockCheck() {
  const [status, setStatus] = useState<StockCheckStatus>('checking');
  const [attempt, setAttempt] = useState(0);
  const syncVariant = useCartStore((s) => s.syncVariant);

  useEffect(() => {
    const cartItems = useCartStore.getState().items;
    if (cartItems.length === 0) {
      setStatus('done');
      return;
    }

    let settled = false;
    let cancelled = false;
    setStatus('checking');

    const finish = (next: StockCheckStatus) => {
      if (settled || cancelled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      setStatus(next);
    };

    const timeoutId = window.setTimeout(() => finish('failed'), STOCK_CHECK_TIMEOUT_MS);
    const productIds = Array.from(new Set(cartItems.map((i) => i.productId)));

    Promise.all(productIds.map((id) => storeService.getProduct(id).catch(() => null))).then((products) => {
      if (settled || cancelled) return;

      const currentItems = useCartStore.getState().items;
      let hasFailure = false;

      products.forEach((product, index) => {
        if (!product) {
          hasFailure = true;
          return;
        }
        product.variants.forEach((variant) => {
          if (currentItems.some((i) => i.variantId === variant.id)) {
            syncVariant(variant.id, variant.stockQuantity, variant.variationTypeName);
          }
        });
        currentItems
          .filter(
            (i) =>
              i.productId === productIds[index] &&
              i.variantId !== NO_VARIANT_ID &&
              !product.variants.some((v) => v.id === i.variantId)
          )
          .forEach((i) => syncVariant(i.variantId, 0));
      });

      finish(hasFailure ? 'failed' : 'done');
    });

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [attempt, syncVariant]);

  const retry = () => setAttempt((a) => a + 1);

  return { status, retry };
}
